import {useRespondToAliveUpdate} from '../../hooks/use-refetch-on-alive-update'
import {Button, IconButton} from '@primer/react'
import {useState} from 'react'
import styles from './RefreshButton.module.css'
import {SyncIcon} from '@primer/octicons-react'
import {SoftNavOnlyLink as RouterLink, useLocation} from '@github-ui/react-router'
import {useComparisonActions} from '../../stores/layout-selectors'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useQueryClient} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'

export function RefreshButton({aliveChannel}: {aliveChannel: string}) {
  const [show, setShow] = useState(false)
  const {markComparisonOutdated, clearComparisonOutdated} = useComparisonActions()
  const updatedHeader = isFeatureEnabled('updated_pull_request_header')
  const queryClient = useQueryClient()

  const liveUpdateHandler = () => {
    markComparisonOutdated()
    setShow(true)
  }

  // we only need to display the refresh button when the base or head sha has changed (via git_updated)
  useRespondToAliveUpdate(aliveChannel, liveUpdateHandler, {git_updated: true})

  if (!show) {
    return null
  }

  const handleRefresh = () => {
    clearComparisonOutdated()
    // Per-item batch revalidation reads fresh per-thread `isOutdated` and per-comment
    // `automatedComment.suggestionState`. Those come from the aggressively-cached (`staleTime:
    // Infinity`) `threads` / `thread_previews` markers and the `thread_preview_positioning`
    // endpoint, none of which a same-route Refresh soft-nav would otherwise refetch. Invalidate all
    // three so the reactive reconciler (`useRevalidateBatchOutdated`) re-applies statuses against
    // fresh data once the refetches land. Gated so no request fires for users without the panel.
    //
    // This blanket invalidation is deliberately broad for now; we plan to narrow it to only the
    // queries backing batched items in github/copilot-discovery-team#1014.
    if (isFeatureEnabled('unified_batch_pr_comments')) {
      void queryClient.invalidateQueries({queryKey: [PageData.threads]})
      void queryClient.invalidateQueries({queryKey: [PageData.threadPreviews]})
      void queryClient.invalidateQueries({queryKey: [PageData.threadPreviewPositioning]})
    }
    if (updatedHeader) {
      setShow(false)
    }
  }

  return <DataRouterRefreshButton onRefresh={handleRefresh} />
}

function DataRouterRefreshButton({onRefresh}: {onRefresh: () => void}) {
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(false)
  const updatedHeader = isFeatureEnabled('updated_pull_request_header')

  const onClick = () => {
    setIsLoading(true)
    onRefresh()
  }

  const sharedProps = {
    as: RouterLink,
    to: location.pathname,
    variant: 'invisible' as const,
    size: 'small' as const,
    onClick,
    loading: isLoading,
    preventScrollReset: true,
    'data-refresh-button-visible': true,
  }

  return updatedHeader ? (
    <>
      <IconButton
        {...sharedProps}
        icon={SyncIcon}
        aria-label="Refresh"
        className={`${styles.refresh} d-flex d-md-none`}
      />
      <Button
        {...sharedProps}
        leadingVisual={SyncIcon}
        aria-label="Refresh"
        className={`${styles.refresh} d-none d-md-flex`}
      >
        Refresh
      </Button>
    </>
  ) : (
    <Button {...sharedProps} leadingVisual={SyncIcon} aria-label="Refresh" className={`${styles.refresh}`}>
      Refresh
    </Button>
  )
}
